import React, { useContext, useEffect, useCallback, useRef } from 'react'
import gsap from 'gsap'
import PropTypes from 'prop-types'
import { StoreState } from '../../Store'

// SVGs
import ExpeditionRouteCircle from '../svgs/ExpeditionRoutes/border'
import ExpeditionRouteMarker from '../svgs/ExpeditionRoutes/marker'
import ExpeditionRoute1 from '../svgs/ExpeditionRoutes/4/1'
import ExpeditionRoute2 from '../svgs/ExpeditionRoutes/4/2'
import ExpeditionRoute3 from '../svgs/ExpeditionRoutes/4/3'
import ExpeditionRoute4 from '../svgs/ExpeditionRoutes/4/4'

// Styles
import {
  SingleRouteMain,
  SingleRoutePath,
  SingleRouteLabel,
  SingleRouteLocation,
  SingleRoutePhoto,
} from './index.style'

// Images
import Valencia from '../Map/assets/locations/valencia.jpg'
import Tallinn from '../Map/assets/locations/tallinn.jpg'
import Reykjavik from '../Map/assets/locations/reykjavik.jpg'

const Route4 = ({ id, backgroundColor, labelColor }) => {
  const store = useContext(StoreState)
  const { waiting } = store

  const routeIn = useRef()

  const line1Ref = {
      line: useRef(),
      arrow: useRef(),
    },
    line2Ref = {
      line: useRef(),
      arrow: useRef(),
    },
    line3Ref = {
      line: useRef(),
      arrow: useRef(),
    },
    line4Ref = {
      line: useRef(),
    },
    circle1Ref = useRef(),
    circle2Ref = useRef(),
    circle3Ref = useRef(),
    photo1Ref = useRef(),
    photo2Ref = useRef(),
    photo3Ref = useRef(),
    label1Ref = useRef(),
    label2Ref = useRef(),
    label3Ref = useRef()

  const play = useCallback(() => {
    routeIn.current.play(0)
  }, [routeIn])

  useEffect(() => {
    if (!waiting) setTimeout(play, 500)
  }, [waiting, play])

  useEffect(() => {
    if (routeIn.current) return

    routeIn.current = gsap.timeline({
      paused: true,
    })

    routeIn.current.to(line1Ref.line.current, {
      ease: 'power1.inOut',
      strokeDashoffset: 198,
      duration: 1,
    })

    routeIn.current.to(
      line1Ref.arrow.current,
      {
        ease: 'power2.out',
        opacity: 1,
        duration: 0.4,
      },
      '=-.3'
    )

    routeIn.current.addLabel('location1', '-=.5')

    routeIn.current.to(
      circle1Ref.current,
      {
        ease: 'power2.inOut',
        strokeDashoffset: 0,
        duration: 1.5,
      },
      'location1'
    )

    routeIn.current.to(
      photo1Ref.current,
      {
        ease: 'power1.inOut',
        opacity: 1,
        duration: 1,
      },
      'location1+=.5'
    )

    routeIn.current.to(
      label1Ref.current,
      {
        ease: 'power2.out',
        opacity: 1,
        y: 0,
        duration: 0.8,
      },
      'location1+=.7'
    )

    // Line 2

    routeIn.current.to(
      line2Ref.line.current,
      {
        ease: 'power1.inOut',
        strokeDashoffset: 542,
        duration: 1.9,
      },
      '=-.6'
    )

    routeIn.current.to(
      line2Ref.arrow.current,
      {
        ease: 'power2.out',
        opacity: 1,
        duration: 0.4,
      },
      '=-.3'
    )

    routeIn.current.addLabel('location2', '-=.5')

    routeIn.current.to(
      circle2Ref.current,
      {
        ease: 'power2.inOut',
        strokeDashoffset: 0,
        duration: 1.5,
      },
      'location2'
    )

    routeIn.current.to(
      photo2Ref.current,
      {
        ease: 'power1.inOut',
        opacity: 1,
        duration: 1,
      },
      'location2+=.5'
    )

    routeIn.current.to(
      label2Ref.current,
      {
        ease: 'power2.out',
        opacity: 1,
        y: 0,
        duration: 0.8,
      },
      'location2+=.7'
    )

    // Line 3

    routeIn.current.to(
      line3Ref.line.current,
      {
        ease: 'power1.inOut',
        strokeDashoffset: 382,
        duration: 1.5,
      },
      '=-.6'
    )

    routeIn.current.to(
      line3Ref.arrow.current,
      {
        ease: 'power2.out',
        opacity: 1,
        duration: 0.4,
      },
      '=-.3'
    )

    routeIn.current.addLabel('location3', '-=.5')

    routeIn.current.to(
      circle3Ref.current,
      {
        ease: 'power2.inOut',
        strokeDashoffset: 0,
        duration: 1.5,
      },
      'location3'
    )

    routeIn.current.to(
      photo3Ref.current,
      {
        ease: 'power1.inOut',
        opacity: 1,
        duration: 1,
      },
      'location3+=.5'
    )

    routeIn.current.to(
      label3Ref.current,
      {
        ease: 'power2.out',
        opacity: 1,
        y: 0,
        duration: 0.8,
      },
      'location3+=.7'
    )

    // Line 4

    routeIn.current.to(
      line4Ref.line.current,
      {
        ease: 'power1.inOut',
        strokeDashoffset: 437,
        duration: 1.7,
      },
      '=-.6'
    )
  }, [
    routeIn,
    line1Ref.line,
    line1Ref.arrow,
    line2Ref.line,
    line2Ref.arrow,
    line3Ref.line,
    line3Ref.arrow,
    line4Ref.line,
    line4Ref.arrow,
    circle1Ref,
    circle2Ref,
    circle3Ref,
    photo1Ref,
    photo2Ref,
    photo3Ref,
    label1Ref,
    label2Ref,
    label3Ref,
  ])

  return (
    <SingleRouteMain>
      <SingleRoutePath right={0} top={27.6} width={22.4}>
        <ExpeditionRoute1
          forwardRef={line1Ref}
          mask={backgroundColor}
          stroke={labelColor}
        />
      </SingleRoutePath>
      <SingleRouteLocation right={11.1} top={44}>
        <SingleRouteLabel ref={label1Ref} left={true} color={labelColor}>
          Valencia <ExpeditionRouteMarker fill={labelColor} />
        </SingleRouteLabel>
        <ExpeditionRouteCircle
          forwardRef={circle1Ref}
          stroke={labelColor}
          rotation={-105}
        />
        <SingleRoutePhoto ref={photo1Ref} bgImage={Valencia} />
      </SingleRouteLocation>
      <SingleRoutePath right={0} top={52} width={50.6}>
        <ExpeditionRoute2
          forwardRef={line2Ref}
          mask={backgroundColor}
          stroke={labelColor}
        />
      </SingleRoutePath>
      <SingleRouteLocation right={36.1} top={65.2}>
        <SingleRouteLabel ref={label2Ref} left={true} color={labelColor}>
          Tallinn <ExpeditionRouteMarker fill={labelColor} />
        </SingleRouteLabel>
        <ExpeditionRouteCircle
          forwardRef={circle2Ref}
          stroke={labelColor}
          rotation={54}
        />
        <SingleRoutePhoto ref={photo2Ref} bgImage={Tallinn} />
      </SingleRouteLocation>
      <SingleRoutePath right={43.7} top={29.9} width={46}>
        <ExpeditionRoute3
          forwardRef={line3Ref}
          mask={backgroundColor}
          stroke={labelColor}
        />
      </SingleRoutePath>
      <SingleRouteLocation right={84.9} top={30.7}>
        <SingleRouteLabel ref={label3Ref} right={true} color={labelColor}>
          <ExpeditionRouteMarker fill={labelColor} /> Reykjavik
        </SingleRouteLabel>
        <ExpeditionRouteCircle
          forwardRef={circle3Ref}
          stroke={labelColor}
          rotation={60}
        />
        <SingleRoutePhoto ref={photo3Ref} bgImage={Reykjavik} />
      </SingleRouteLocation>
      <SingleRoutePath right={80.8} top={-5.7} width={22.7}>
        <ExpeditionRoute4
          forwardRef={line4Ref}
          mask={backgroundColor}
          stroke={labelColor}
        />
      </SingleRoutePath>
    </SingleRouteMain>
  )
}

Route4.propTypes = {
  id: PropTypes.string.isRequired,
  backgroundColor: PropTypes.string,
  labelColor: PropTypes.string,
}

export default Route4
